import { useCallback } from 'react';
import { useDaoConfigStore } from '@/stores/daoConfigStore';

/**
 * useDaoConfig - Unified hook to access and format DAO-dependent terminology and configurations.
 *
 * Example usage:
 * ```tsx
 * const { workerRole, clientRole, currency, formatCurrency } = useDaoConfig();
 *
 * return (
 *   <div>
 *     <h2>{workerRole} Login</h2>
 *     <p>Budget: {formatCurrency(1500)}</p>
 *   </div>
 * );
 * ```
 */
export function useDaoConfig() {
  const config = useDaoConfigStore((s) => s.config);
  const loadDaoConfig = useDaoConfigStore((s) => s.loadDaoConfig);
  const setDaoConfig = useDaoConfigStore((s) => s.setDaoConfig);
  const updateDaoConfig = useDaoConfigStore((s) => s.updateDaoConfig);
  const isLoading = useDaoConfigStore((s) => s.isLoading);
  const error = useDaoConfigStore((s) => s.error);

  const terminology = config.terminology;
  const currency = config.currency;

  /**
   * Helper to format an amount with the current DAO's currency symbol.
   * e.g. 1500 -> "1500 KVN" (or "1500 USDC")
   */
  const formatCurrency = useCallback(
    (amount: number | string | bigint | undefined | null): string => {
      if (amount === undefined || amount === null) return `0 ${currency.symbol}`;
      return `${amount} ${currency.symbol}`;
    },
    [currency.symbol]
  );

  return {
    /** The full active DAO configuration object */
    config,
    /** Terminology dictionary for labels, roles, and instructions */
    terminology,
    /** Currency settings (symbol, name, decimals) */
    currency,
    /** Formats a numerical amount with the active DAO currency */
    formatCurrency,

    // Ergonomic shorthand aliases
    workerRole: terminology.workerRole,
    workerRolePlural: terminology.workerRolePlural,
    clientRole: terminology.clientRole,
    coordinatorRole: terminology.coordinatorRole,
    workerPitch: terminology.workerPitch,
    clientPitch: terminology.clientPitch,
    externalProfile: terminology.externalProfile,
    skillsHelperText: terminology.skillsHelperText,
    backgroundHelperText: terminology.backgroundHelperText,
    emptyStateWorkerText: terminology.emptyStateWorkerText,
    emptyStateClientText: terminology.emptyStateClientText,
    projectTypes: config.projectTypes,
    daoName: config.name,
    daoTagline: config.tagline,

    // Store mutations
    loadDaoConfig,
    setDaoConfig,
    updateDaoConfig,
    isLoading,
    error,
  };
}
